<template>
	<div v-if="activeTab" v-loading="loading">
		<MiniTable :data="pickCurrTableData" :key="activeTab">
			<!-- 分红送配 -->
			<template v-if="activeTab === 'dividendPlan'">
				<MiniTableColumn prop="publishDate" :label="$t('enterprise.announced')" width="100" />
				<MiniTableColumn prop="recordDate" :label="$t('enterprise.record-date')" width="120" />
				<MiniTableColumn prop="dividendDate" :label="$t('enterprise.payment-date')" width="120" />
				<MiniTableColumn prop="dividendCash" :label="$t('enterprise.dividend-scheme')" width="150" />
				<MiniTableColumn prop="perShareDivRatio" :label="$t('enterprise.bonus-issue/by-reserve')" width="150">
					<template #default="{ row }">
						<span
							>{{ formatDefault(row.perShareDivRatio) }}/{{ formatDefault(row.perShareTransRatio) }}</span
						>
					</template>
				</MiniTableColumn>
				<MiniTableColumn prop="exDivDate" :label="$t('enterprise.ex-date')" width="120" />
			</template>
			<!-- 融资融券 -->
			<template v-if="activeTab === 'marginFunds'">
				<MiniTableColumn prop="tradingDay" :label="$t('enterprise.trading-day')" width="100" />
				<MiniTableColumn prop="financeValue" :label="$t('enterprise.current-financing-balance')" width="180">
					<template #default="{ row }">
						<span>{{ formatterColTransferUnit(row, { property: 'financeValue' }) }}</span>
					</template>
				</MiniTableColumn>
				<MiniTableColumn
					prop="financeBuyValue"
					:label="$t('enterprise.current-financing-purchase-amount')"
					width="180"
				>
					<template #default="{ row }">
						<span>{{ formatterColTransferUnit(row, { property: 'financeBuyValue' }) }}</span>
					</template>
				</MiniTableColumn>
				<MiniTableColumn
					prop="securityVolume"
					:label="$t('enterprise.current-short-selling-balance')"
					width="180"
				>
					<template #default="{ row }">
						<span>{{ formatterColTransferUnit(row, { property: 'securityVolume' }) }}</span>
					</template>
				</MiniTableColumn>
				<MiniTableColumn
					prop="securitySellVolume"
					:label="$t('enterprise.current-short-selling-volume')"
					width="250"
				>
					<template #default="{ row }">
						<span>{{ formatterColTransferThousand(row, { property: 'securitySellVolume' }) }}</span>
					</template>
				</MiniTableColumn>
				<MiniTableColumn
					prop="securityValue"
					:label="$t('enterprise.short-selling-balance-amount')"
					width="180"
				>
					<template #default="{ row }">
						<span>{{ formatterColTransferUnit(row, { property: 'securityValue' }) }}</span>
					</template>
				</MiniTableColumn>
				<MiniTableColumn prop="tradingValue" :label="$t('enterprise.securities-lending-balance')" width="130">
					<template #default="{ row }">
						<span>{{ formatterColTransferUnit(row, { property: 'tradingValue' }) }}</span>
					</template>
				</MiniTableColumn>
			</template>
			<!-- 北上/南下资金 -->
			<template v-if="activeTab === 'northSouthFunds'">
				<MiniTableColumn prop="tradingDay" :label="$t('enterprise.holding-date')" width="100" />
				<MiniTableColumn prop="closePrice" :label="$t('enterprise.closing-price')" width="180">
					<template #default="{ row }">
						<span>{{ formatterColTransferThousand(row, { property: 'closePrice' }) }}</span>
					</template>
				</MiniTableColumn>
				<MiniTableColumn prop="changePct" :label="$t('enterprise.daily-change')" width="180" />
				<MiniTableColumn prop="holdVol" :label="$t('enterprise.number-of-shares-held-(shares)')" width="180">
					<template #default="{ row }">
						<span>{{ formatterColTransferUnit(row, { property: 'holdVol' }) }}</span>
					</template>
				</MiniTableColumn>
				<MiniTableColumn prop="holdPct" :label="$t('enterprise.holding-percentage')" width="180" />
			</template>
		</MiniTable>
		<FooterLoadPlaceholder :more="hasMoreData" @loadMore="onPullUp" />
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, watch } from 'vue';
import { useStockVO } from '@rebuild/hooks/useStockVO';
import { usePaginationMobile } from '@rebuild/hooks/load-more/usePaginationMobile';
import { formatDefault, isNonValue, convertNumberToCNUnit } from '@/utils/api';
import i18n, { EN } from '@/locales';
import { useModuleData } from '@/rebuild/hooks/data/useModuleData';
import { MiniTable, MiniTableColumn } from '@/rebuild/components/table';
import FooterLoadPlaceholder from '@/rebuild/components/FooterLoadPlaceholder.vue';

const props = defineProps({
	activeTab: {
		type: String,
		default: ''
	}
});

const { proxy } = getCurrentInstance();
const { isHKStock } = useStockVO();

const handlePaginationChange = () => {
	updateDataTab({ value: props.activeTab });
};
const {
	currentPage,
	pageSize,

	finalData,
	setData,
	hasMoreData,
	onPullUp,
	refreshPagination
} = usePaginationMobile({
	bindChange: handlePaginationChange
});

const { quote_stockDividendPlan, quote_stockMarginFunds, quote_stockNorthSouthFunds } = useModuleData();

const queryDividendPlan = async () => {
	await quote_stockDividendPlan.refresh();
	return {
		label: proxy.$t('enterprise.dividends'),
		prop: 'dividendPlan',
		data: quote_stockDividendPlan.data.data || []
	};
};
const queryMarginFunds = async () => {
	await quote_stockMarginFunds.refresh({
		pageStart: currentPage.value - 1,
		pageNum: pageSize.value
	});
	return {
		label: '融资融券',
		prop: 'marginFunds',
		data: quote_stockMarginFunds.data.data || [],
		total: quote_stockMarginFunds.data.total || 0
	};
};

const queryMorthSouthFunds = async () => {
	const composeLabel = isHKStock.value
		? proxy.$t('enterprise.southbound-capital')
		: proxy.$t('enterprise.northbound-funds');

	await quote_stockNorthSouthFunds.refresh({
		pageStart: currentPage.value - 1,
		pageNum: pageSize.value
	});
	return {
		label: composeLabel,
		prop: 'northSouthFunds',
		data: quote_stockNorthSouthFunds.data.data || [],
		total: quote_stockNorthSouthFunds.data.total || 0
	};
};

const pickCurrTableData = computed(() => {
	return finalData.value || [];
});

function recognizeUnit(numStr) {
	var num = parseFloat(numStr);
	if (i18n.locale === EN) {
		const { value, unit } = convertNumberToCNUnit(num);
		return `${value}${unit}`;
	}

	var unit = proxy.$t('enterprise.ten-thousand');
	var value = num / 10000;
	if (num >= 100000000) {
		unit = proxy.$t('enterprise.one-hundred-million');
		value = num / 100000000;
	}
	return `${value.toFixed(2)}${unit}`;
}
const formatterColTransferUnit = (row, column) => {
	const nonValue = isNonValue(row[column.property]);
	if (nonValue) return formatDefault(row[column.property]);
	return recognizeUnit(row[column.property]);
};
function formatDecimal(inputString) {
	// 判断字符串中是否包含小数点
	if (inputString.includes('.')) {
		// 分割小数点前后的部分
		const [integerPart, decimalPart] = inputString.split('.');

		// 补零，确保小数点后至少有两位数
		let formattedDecimal = decimalPart;
		if (formattedDecimal.length === 1) {
			formattedDecimal += '0';
		}

		// 拼接整数部分和格式化后的小数部分
		return `${integerPart}.${formattedDecimal}`;
	} else {
		// 不包含小数点，直接返回原字符串
		return inputString;
	}
}
function toThousandSeparator(num) {
	const value = Number(num).toLocaleString();
	return formatDecimal(value);
}
const formatterColTransferThousand = (row, column) => {
	const nonValue = isNonValue(row[column.property]);
	if (nonValue) return formatDefault(row[column.property]);
	return toThousandSeparator(row[column.property]);
};

const loading = computed(() => {
	if (props.activeTab == 'dividendPlan') {
		return quote_stockDividendPlan.loading;
	}
	if (props.activeTab == 'marginFunds') {
		return quote_stockMarginFunds.loading;
	}
	if (props.activeTab == 'northSouthFunds') {
		return quote_stockNorthSouthFunds.loading;
	}
	return false;
});

const updateDataTab = result => {
	const { value } = result;
	if (value == 'dividendPlan') {
		return queryDividendPlan().then(res => {
			setData(res.data, 0);
		});
	}
	if (value == 'marginFunds') {
		return queryMarginFunds().then(res => {
			setData(res.data, res.total);
			return res.total;
		});
	}
	if (value == 'northSouthFunds') {
		return queryMorthSouthFunds().then(res => {
			setData(res.data, res.total);
			return res.total;
		});
	}
};

// 监听activeTab变化，更新数据
watch(
	() => props.activeTab,
	newVal => {
		if (newVal) {
			refreshPagination();
		}
	},
	{ immediate: true }
);
</script>

<style lang="less" scoped>
.c-stock-table {
	position: relative;
}
.unit-tips {
	font-size: 14px;
	font-weight: 400;
	color: #86909c;
	height: 55px;
	line-height: 55px;

	position: absolute;
	top: 0;
	right: 0;
}
.el-table {
	/deep/th.el-table__cell {
		.cell {
			color: #fff !important;
		}
	}
}
</style>
